/**
 * Result table for the offer picker — extracted so the picker shell stays
 * inside the 150-line budget.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';

import { Checkbox } from '@/components/ui/form/controls/Checkbox';
import type { BinomOffer } from '@/hooks/binom';

export interface BinomOfferPickerTableProps {
  rows: BinomOffer[];
  loading: boolean;
  selected: Record<string, boolean>;
  onToggle: (id: string, v: boolean) => void;
  groupNameByUuid: Map<string, string>;
}

const BinomOfferPickerTable: FC<BinomOfferPickerTableProps> = ({
  rows,
  loading,
  selected,
  onToggle,
  groupNameByUuid,
}) => (
  <div className="max-h-[24rem] overflow-y-auto rounded-md border border-border/40">
    <table className="w-full text-xs">
      <thead className="sticky top-0 bg-muted/30">
        <tr className="text-left text-[10px] uppercase tracking-wide text-muted-foreground">
          <th className="w-8 px-2 py-1.5"></th>
          <th className="w-12 px-2 py-1.5">↓ ID</th>
          <th className="px-2 py-1.5">Name</th>
          <th className="w-32 px-2 py-1.5">Group</th>
          <th className="w-16 px-2 py-1.5">Country</th>
        </tr>
      </thead>
      <tbody>
        {loading ? (
          <tr>
            <td colSpan={5} className="p-3 text-muted-foreground">Loading…</td>
          </tr>
        ) : rows.length === 0 ? (
          <tr>
            <td colSpan={5} className="p-3 text-muted-foreground">
              No matching offers.
            </td>
          </tr>
        ) : (
          rows.map((o) => (
            <tr key={o.id} className="border-t border-border/30 hover:bg-muted/20">
              <td className="px-2 py-1.5">
                <Checkbox
                  checked={Boolean(selected[o.id])}
                  onCheckedChange={(v) => onToggle(o.id, Boolean(v))}
                />
              </td>
              <td className="px-2 py-1.5 tabular-nums text-muted-foreground">
                {o.id}
              </td>
              <td className="truncate px-2 py-1.5">{o.name}</td>
              <td className="truncate px-2 py-1.5 text-muted-foreground">
                {groupNameByUuid.get(
                  String((o as { groupUuid?: string }).groupUuid ?? ''),
                ) ?? ''}
              </td>
              <td className="px-2 py-1.5 text-muted-foreground">
                {o.countryCode ?? ''}
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

export default BinomOfferPickerTable;
